import { getApps, initializeApp, type FirebaseApp } from 'firebase/app';
import {
  GoogleAuthProvider,
  browserLocalPersistence,
  browserPopupRedirectResolver,
  browserSessionPersistence,
  createUserWithEmailAndPassword,
  indexedDBLocalPersistence,
  initializeAuth,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  updateProfile,
  type Auth,
} from 'firebase/auth';
import { heldToken, holdToken } from './held-token';

/**
 * Identity Platform on project paige-v2-dev (architecture §14.1). One sign-in for everyone: email
 * and password, or Google. Whether a person is Ademero staff is decided by the API from the
 * verified account domain, never by how they signed in. The web API key comes from
 * `VITE_IDP_API_KEY` (apps/web/.env.local; see .env.example); it is a browser key, restricted by
 * referrer, and never committed.
 */
/**
 * The identity project and tenant belong to the environment the bundle is built for: dev by
 * default; production's are `paige-v2-prod` and its tenant, set with `VITE_IDP_PROJECT` and
 * `VITE_IDP_TENANT` at build time next to the key (docs/runbooks/deploy.md B.2). Until 2026-09-21
 * they were fixed to dev, so a production build sent dev's tenant with prod's key and every
 * sign-in on v2.paige.app failed.
 */
function fromEnv(name: string, fallback: string): string {
  const value = import.meta.env[name];
  return typeof value === 'string' && value.length > 0 ? value : fallback;
}

export const idpProject = fromEnv('VITE_IDP_PROJECT', 'paige-v2-dev');
export const idpTenant = fromEnv('VITE_IDP_TENANT', 'customers-9fiwy');

/**
 * The host that serves Identity Platform's auth helper (`/__/auth/handler`, `/__/auth/iframe`).
 * Google's account chooser names it ("to continue to ..."), so a deployed site sets its own host
 * with `VITE_IDP_AUTH_DOMAIN` (`v2.paige.app`, `dev.v2.paige.app`) and its web image proxies
 * `/__/auth/` to the project's firebaseapp.com (infra/preview/nginx.conf). Unset, the project's own
 * firebaseapp.com, which is what a local build uses: localhost cannot host the helper over https.
 */
export const idpAuthDomain = fromEnv('VITE_IDP_AUTH_DOMAIN', `${idpProject}.firebaseapp.com`);

export function idpApiKey(): string | null {
  const key = import.meta.env['VITE_IDP_API_KEY'];
  return typeof key === 'string' && key.length > 0 ? key : null;
}

function app(): FirebaseApp {
  const existing = getApps()[0];
  if (existing) return existing;
  const apiKey = idpApiKey();
  if (!apiKey) throw new Error('VITE_IDP_API_KEY is not set');
  return initializeApp({
    apiKey,
    authDomain: idpAuthDomain,
    projectId: idpProject,
  });
}

let instance: Auth | null = null;

/**
 * The one Auth instance, made here with the tenant set before anything reads it: never `getAuth`,
 * whose instance any other caller could make first without the tenant (`signOutIdp` did).
 */
function auth(): Auth {
  if (instance) return instance;
  const a = initializeAuth(app(), {
    persistence: [indexedDBLocalPersistence, browserLocalPersistence, browserSessionPersistence],
    popupRedirectResolver: browserPopupRedirectResolver,
  });
  a.tenantId = idpTenant;
  instance = a;
  return a;
}

/** The Identity Platform handle for the second-step flows (second-factor.ts). */
export function idpAuth(): Auth {
  return auth();
}

/** Email and password; resolves to the ID token the API exchanges for a session. */
export async function signInWithPassword(email: string, password: string): Promise<string> {
  const cred = await signInWithEmailAndPassword(auth(), email, password);
  return holdToken(await cred.user.getIdToken());
}

/** Google, any account; the API reads the Google-signed hosted-domain claim to recognise staff. */
export async function signInWithGoogle(): Promise<string> {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: 'select_account' });
  const cred = await signInWithPopup(auth(), provider);
  return holdToken(await cred.user.getIdToken());
}

/** Creates the identity for a new person; resolves to the ID token `POST /signup` needs. */
export async function createAccount(
  name: string,
  email: string,
  password: string,
): Promise<string> {
  const cred = await createUserWithEmailAndPassword(auth(), email, password);
  if (name.trim()) await updateProfile(cred.user, { displayName: name.trim() });
  return holdToken(await cred.user.getIdToken(true));
}

/**
 * The ID token of whoever is signed into Identity Platform in this browser, if anyone; else the
 * token this page's own sign-in returned while it is still valid. Prod 2026-10-05: right after a
 * Google sign-in on /sign-in and the move to the company step, `currentUser` was null, and the step
 * could not name the company; the page had the token all along.
 */
export async function currentIdToken(): Promise<string | null> {
  if (!idpApiKey()) return null;
  const a = auth();
  await a.authStateReady();
  return a.currentUser ? a.currentUser.getIdToken() : heldToken();
}

/** Signs out of Identity Platform; the API session is cleared separately. */
export async function signOutIdp(): Promise<void> {
  holdToken(null);
  if (!idpApiKey() || getApps().length === 0) return;
  await signOut(auth());
}
